/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* =============================================================================
   HOUSE OF AMANI — Custom Behaviors
   ============================================================================= */

// Header: blur after 100px scroll, hide on scroll down, reveal on scroll up
(function () {
  const header = document.querySelector('.section-header');
  if (!header) return;

  let lastY = window.scrollY;
  let rafPending = false;

  function onScroll() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      const y = window.scrollY;
      header.classList.toggle('hoa-scrolled', y > 100);
      if (y > lastY && y > 100) {
        header.classList.add('hoa-hidden');
      } else {
        header.classList.remove('hoa-hidden');
      }
      lastY = y;
      rafPending = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
})();

// Tailor made variant — flag l'option dans le sélecteur Size et révèle le hint
// quand elle est sélectionnée. Marche pour fiche produit, featured-product, quickbuy.
(function () {
  var TM_VALUES = ['tailor made', 'tailor-made', 'sur mesure'];

  function normalize(s) {
    return (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function isTailorMade(value) {
    return TM_VALUES.indexOf(normalize(value)) !== -1;
  }

  // Pose .hoa-tm-option sur tous les labels "Tailor made" du document.
  // Scope = document parce que Symmetry rend <variant-selects> en dehors du
  // <form>. Le label visible a la structure :
  //   <label class="opt-label opt-label--btn ..."><span class="js-value">Tailor made</span></label>
  function tagOptions(root) {
    root = root || document;

    // (a) Labels / .opt-label par texte (couvre le cas Symmetry)
    root.querySelectorAll('.opt-label').forEach(function (label) {
      if (isTailorMade(label.textContent)) {
        label.classList.add('hoa-tm-option');
      }
    });

    // (b) <select> options
    root.querySelectorAll('select option').forEach(function (opt) {
      if (isTailorMade(opt.value) || isTailorMade(opt.textContent)) {
        opt.classList.add('hoa-tm-option');
      }
    });
  }

  // Pour un input donné, retourne le <form> associé : soit son ancêtre direct,
  // soit le form référencé via l'attribut form="..." (cas Symmetry où les
  // <variant-selects> sont hors du form).
  function formOf(input) {
    return input.form || (input.closest && input.closest('form[action*="/cart/add"]'));
  }

  // Lit l'état courant et toggle .hoa-tm-selected sur le form ET sur <body>.
  // Le body-level permet au bloc .hoa-tm-hint d'être placé n'importe où dans
  // la page (y compris hors du form) et de réagir.
  function syncState(form) {
    if (!form) return;
    var isTM = false;

    document.querySelectorAll('input[type="radio"]:checked').forEach(function (r) {
      if (formOf(r) === form && isTailorMade(r.value)) isTM = true;
    });
    document.querySelectorAll('select').forEach(function (s) {
      if (formOf(s) === form && s.value && isTailorMade(s.value)) isTM = true;
    });

    form.classList.toggle('hoa-tm-selected', isTM);
    syncBodyState();
  }

  // Pose .hoa-tm-selected sur <body> si AU MOINS un form de la page a TM
  // sélectionné. Évite la dépendance à un ancêtre commun form/hint.
  function syncBodyState() {
    var any = !!document.querySelector('form[action*="/cart/add"].hoa-tm-selected');
    document.body.classList.toggle('hoa-tm-selected', any);
  }

  function initAll() {
    // Le tagging est idempotent (classList.add) — on l'exécute une fois ici,
    // puis le MutationObserver s'occupe des labels qui apparaîtraient plus tard.
    tagOptions(document);
    document
      .querySelectorAll('form[action*="/cart/add"]')
      .forEach(syncState);
  }

  // Re-sync à chaque changement de variante. On NE retire/réapplique PAS la
  // classe .hoa-tm-option (elle reste posée une fois pour toutes). On toggle
  // uniquement .hoa-tm-selected sur le form + <body>.
  document.addEventListener('on:variant:change', function (event) {
    var selectedOptions = (event.detail && event.detail.selectedOptions) || [];
    var form =
      (event.detail && event.detail.form && event.detail.form.closest
        ? event.detail.form.closest('form')
        : null) ||
      (event.target && event.target.closest
        ? event.target.closest('form[action*="/cart/add"]')
        : null);

    if (!form) {
      document.querySelectorAll('form[action*="/cart/add"]').forEach(syncState);
      return;
    }

    // Symmetry re-render les labels à chaque variant change → on re-tagge.
    // classList.add est idempotent, donc no-op si déjà posé.
    tagOptions(document);

    var isTM = selectedOptions.some(isTailorMade);
    form.classList.toggle('hoa-tm-selected', isTM);
    syncBodyState();
  });

  // Quickbuy : le form est injecté après ouverture du drawer
  document.addEventListener('on:quickbuy:after-open', initAll);

  // --- Initial state checks ---------------------------------------------------
  // Symmetry hydrate ses web components après le DOMContentLoaded ; les radios
  // peuvent n'avoir leur attribut :checked que plus tard. On resync :
  //  - au DOMContentLoaded
  //  - au window.load (toutes ressources chargées)
  //  - via MutationObserver tant que le form évolue (limite : 5s après init)
  function resyncAll() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(syncState);
  }

  function observeForms() {
    // On observe document.body en permanence : Symmetry re-render les
    // <variant-selects> à chaque changement de variante, ce qui détruit puis
    // recrée les labels. Sans observer persistant, .hoa-tm-option disparaît.
    // tagOptions est idempotent (classList.add) → coût négligeable.
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        // L'observer ne fait QUE re-tagger les labels — l'état de sélection
        // (.hoa-tm-selected) est piloté exclusivement par on:variant:change
        // pour éviter une race avec les états transitoires de re-render.
        tagOptions(document);
      });
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'value', 'aria-checked', 'selected']
    });
  }

  function bootstrap() {
    initAll();
    observeForms();
    // Filet de sécurité : resync 1 frame après init, puis 500ms après
    requestAnimationFrame(resyncAll);
    setTimeout(resyncAll, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
  window.addEventListener('load', resyncAll);
})();

// Scroll indicator: inject animated line into first hero section on homepage
(function () {
  function inject() {
    if (!document.body.classList.contains('template-index')) return;
    const hero = document.querySelector(
      '.shopify-section:nth-child(2) .text-overlay__inner, ' +
      '.shopify-section:nth-child(2) [class*="overlay__content"], ' +
      '.shopify-section:nth-child(2) .banner__inner'
    );
    if (!hero || hero.querySelector('.hoa-scroll-indicator')) return;

    const el = document.createElement('div');
    el.className = 'hoa-scroll-indicator';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="hoa-scroll-indicator__line"></div>' +
      '<span class="hoa-scroll-indicator__label">Scroll</span>';
    hero.style.position = 'relative';
    hero.appendChild(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', inject);
  } else {
    inject();
  }
})();
